import React, { useEffect } from "react";
import { observer } from "mobx-react";
import {
    errorExplanationOptions,
    triggerAnalystCommentModal,
    getCommentIconRenderingInfo,
    getInputRenderingInfo,
    normalizeSelectInput,
    getNormalizedSelectInput
} from '../helpers'
import store from './Stores/SurveyStore'
import { saveValueNew, openPrevYearPanel } from "../control";
import { InputState } from "../Enums/constants";
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Tooltip } from 'react-tippy';
import { handlePlanDesignSelectCase } from "../ibsHelper";
import $ from 'jquery';
import { getSurveyResponseValue, setSurveyResponseValue } from "../Helpers/General/SurveyResponsesHelper";

// Maps a selected dropdown value to its InputStateId so N/A and Unknown
// selections are persisted as states (1 and 2) rather than plain strings.
const getInputStateId = (value) => {
    const normalizedValue = (value ?? "").toString().toUpperCase()
    if (store.local.naList.includes(normalizedValue)) {
        return InputState.NA
    }
    if (store.local.unknownList.includes(normalizedValue)) {
        return InputState.UNKNOWN
    }
    return InputState.NOT_SET
}

const SurveySelect = observer((props) => {
    let optionsGiven = props.options; //dic or list
    let optionsList = []

    let ellipsisForLengthyDD = (val) => {
        let newVal = val
        if (val && val.length > 17) {
            let endIndex = props.isWidePlanInfo ? 22 : 17
            newVal = val.substring(0, endIndex) + "..."
            //This may cause overrides in special cases
            if (!props.participantType) $("." + props.name + " .k-input").text(newVal)
        }
    }

    useEffect(() => {
        ellipsisForLengthyDD(getValue());
    });

    if (props.saveAsKey && optionsGiven && optionsGiven.constructor === Object) {
        // Respect "displayOrder" if present
        if ("displayOrder" in optionsGiven) {
            optionsGiven.displayOrder.forEach((key) => {
                optionsList.push({ value: key, text: optionsGiven[key] });
            });
        } else {
            for (let [key, val] of Object.entries(optionsGiven)) {
                if (key === "displayOrder") continue;
                optionsList.push({ value: key, text: val });
            }
        }
    } else {

        //split dict into list
        if (optionsGiven && optionsGiven.constructor == Object) {
            if ("displayOrder" in optionsGiven) {
                optionsGiven["displayOrder"].forEach(function (number) {
                    optionsList.push(optionsGiven[number])
                })
            } else {
                //keep order of IDs
                for (let key in optionsGiven) {
                    if (key != "displayOrder") optionsList.push(optionsGiven[key])
                    else delete optionsList['displayOrder']
                }
            }

        } else {
            optionsList = optionsGiven
        }
    }

    const getValue = () => {
        try {
            if (store.questionMode) {
                return props.name;
            } else {
                let value = getSurveyResponseValue(props.name, props.rowData);
                value = handlePlanDesignSelectCase(props, value);

                if (props.saveAsKey) {
                    return value; // Match in the <DropDownList> with valueField="value"
                } else {
                    return getNormalizedSelectInput(value, optionsGiven);
                }
            }
        } catch (error) {
            return "";
        }
    };

    let currentKeyOrValue = getValue();
    let selectedItem = currentKeyOrValue;


    if (!store.questionMode && props.saveAsKey && Array.isArray(optionsList)) {
        // If saveAsKey => our data array is in the shape [{ value: <key>, text: <label> }, ...]
        // Unless we are in questionMode
        // If it matches, we have an object { value, text }
        selectedItem = optionsList.find((obj) => obj.value === currentKeyOrValue) || null;
    }

    // On every render, attempt ellipsis
    useEffect(() => {
        ellipsisForLengthyDD(currentKeyOrValue);
    });


    const onChange = (e) => {
        let newSelection = e.target.value;
        let rowId = null;
        let tableName = null;

        //check if plan design
        if (props.participantType != undefined && (props.participantType.toString() == "0" || props.participantType.toString() == "1")) {
            rowId = props.participantType
            tableName = "PlanDesign"
        }

        if (props.saveAsKey) {
            const newKey = newSelection ? newSelection.value : "";
            // saveAsKey stores a key, not the label, so derive the state from the display text.
            const inputStateId = getInputStateId(newSelection ? newSelection.text : "");
            setSurveyResponseValue(props.name, newKey, props);
            saveValueNew(props.name, newKey, { inputStateId, tableName, rowId });
        } else {
            let value = normalizeSelectInput(e.target.value, optionsGiven)
            setSurveyResponseValue(props.name, value, props)
            saveValueNew(props.name, value, { inputStateId: getInputStateId(value), tableName, rowId })
            if (props.name === "IsMandateLevel" && (value == 1 || value == 2)) {
                setSurveyResponseValue("IsAvgAssetsGiv", 1, props)
                saveValueNew("IsAvgAssetsGiv", 1, {})
            }
        }
    }
    const onFocus = () => {
        openPrevYearPanel(props.name);
    }
    const onBlur = () => {
        store.previousYearValue = ""
    }

    let commentRenderingInfo = getCommentIconRenderingInfo(props.name)
    let inputRenderingInfo = getInputRenderingInfo(props.name, props.inputType)
    let popupSettings = {};

    if (props.overrideWidth) {
        popupSettings.width = props.overrideWidth;
    }

    // Handle label truncation
    var longest = "";
    if (Array.isArray(optionsList)) {
        // If objects, get the longest text
        if (props.saveAsKey) {
            longest = optionsList.reduce((acc, item) => (item.text.length > acc.length ? item.text : acc), "");
        } else {
            // If array of strings
            longest = optionsList.reduce((acc, str) => (str.length > acc.length ? str : acc), "");
        }
    }

    const isOverFlowDD = () => {
        if (longest && longest.length > 17) return " surveySelectOverflow"
        return ""
    }

    return (
        <React.Fragment >
            {inputRenderingInfo && inputRenderingInfo.feedbackComment &&
                <Tooltip
                    disabled={store.questionMode}
                    position="top"
                    trigger="mouseenter"
                    animation="fade"
                    delay={250}
                    hideOnClick="false"
                    arrow="true"
                    size="small"
                    interactive="true"
                    html={(
                        <div style={{ width: 175 }} className="genericTooltip">
                            <span dangerouslySetInnerHTML={{ __html: inputRenderingInfo.feedbackComment }}></span>
                            <br />
                            {errorExplanationOptions(props.name, inputRenderingInfo, getValue())}

                        </div>
                    )}
                >

                    <DropDownList
                        disabled={store.questionMode}
                        style={{
                            width: props.isWidePlanInfo ? "230px" : "190px",
                        }}
                        className={
                            (store.questionMode ? "disabledSel surveySelect " : "surveySelect ") +
                            inputRenderingInfo.inputColor + " " +
                            props.name +
                            (props.isWidePlanInfo ? " wideSelect" : " defaultSelect")
                        }
                        disabled={inputRenderingInfo.isDisabled}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        onChange={onChange}
                        data={optionsList}
                        textField={!store.questionMode && props.saveAsKey ? "text" : undefined}
                        valueField={!store.questionMode && props.saveAsKey ? "value" : undefined}
                        value={selectedItem}
                        popupSettings={popupSettings}
                    />

                </Tooltip>
            }

            {inputRenderingInfo == undefined || !inputRenderingInfo.feedbackComment &&
                <DropDownList
                    style={{
                        width: props.isWidePlanInfo ? "230px" : "190px",
                    }}
                    disabled={store.questionMode}
                    className={
                        (store.questionMode ? "disabledSel surveySelect " : "surveySelect ") +
                        inputRenderingInfo.inputColor + isOverFlowDD() + " " +
                        props.name +
                        (props.isWidePlanInfo ? " wideSelect" : " defaultSelect")
                    }
                    disabled={inputRenderingInfo.isDisabled}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onChange={onChange}
                    data={optionsList}
                    textField={!store.questionMode && props.saveAsKey ? "text" : undefined}
                    valueField={!store.questionMode && props.saveAsKey ? "value" : undefined}
                    value={selectedItem}
                    popupSettings={popupSettings}
                />
            }

            {store.analystMode &&
                <a title={commentRenderingInfo.hoverComment}>
                    &nbsp;&nbsp;&nbsp;
                    <i
                        className={"fa fa-comment " + commentRenderingInfo.className}
                        onClick={() => triggerAnalystCommentModal(props.name)}
                        style={{
                            visibility:
                                store.analystMode ||
                                    (store.accountInfo && store.accountInfo.isAnalyst && commentRenderingInfo.className)
                                    ? ""
                                    : "hidden"
                        }}
                    />
                </a>
            }
        </React.Fragment >
    );
});
export default SurveySelect;
//$("button").click(function () {
//    (".surveySelectOverflow .k-input").innerText = "hi..."
//});
//$(".surveySelectOverflow .k-input").innerText = "hi..."
//longestWidth>155 ? longestWidth+"px": 

//var SelectHelper = {
//    truncateSelectedValue: function (parent, callbackfn) {
//        callbackfn($('#elm').css('background-color'), parent);
//    },

//};

let truncateSelectedValue = (isWidePlanInfo) => {
    let valueNew = $(".surveySelectOverflow .k-input")
    console.log(valueNew[0])
    //if (valueNew.length > 17) {
    //    if (isWidePlanInfo) valueNew = valueNew.substring(0, 23) + '...';
    //    else valueNew = valueNew.substring(0, 17) + '...';
    //    $(".surveySelectOverflow .k-input").innerText = valueNew
    //}
}